import { ref } from "vue"
import { BaseModel } from "@/models/base.model"
import { type IBase } from "@/interfaces/api/base.interface"
import { type ModelConstructorSignature } from "@/models/types/model-constructor-signature"
import { v4 as uuidv4 } from "uuid"
import { isProductionEnvironment } from "@/helper/app/browser-helper.ts"

const RESOLUTION_CHECK_INTERVAL_MS = 100

export default class ModelManager {
  private static _instance: ModelManager

  private idModelMap = ref(new Map<string, BaseModel | null>())

  private deletedModelIDs = ref(new Map<string, boolean>())

  private queuedModelUpdates = ref(new Map<string, BaseModel>())

  private pendingModelLinks = ref<Map<string, Array<(model: BaseModel) => void>>>(new Map())

  private constructor() {
    /* Prevent instantiation */
  }

  public static getInstance() {
    if (!this._instance) {
      this._instance = new ModelManager()
    }
    return this._instance
  }

  /**
   * Retrieves a model by its ID from the internal map.
   * @param id - The ID of the model to retrieve.
   * @param callback - Optional callback function that will be called when the model is found.
   */
  public get(id: string, callback?: (model: BaseModel) => void) {
    // Get the model from the map.
    const model = this.idModelMap.value.get(id)

    // If the model is not found, push the callback to the pendingModelLinks map so we can call it later.
    if (model == undefined && callback) {
      if (!this.pendingModelLinks.value.has(id)) {
        this.pendingModelLinks.value.set(id, [])
      }
      this.pendingModelLinks.value.get(id)!.push(callback)
    }
    return model
  }

  // OVERLOAD: create without data will always return ModelType
  public create<ModelType extends BaseModel, DataType extends IBase>(
    modelType: ModelConstructorSignature<DataType, ModelType>,
    data?: DataType | ModelType
  ): ModelType

  // OVERLOAD: create with data will return ModelType | null
  public create<ModelType extends BaseModel, DataType extends IBase>(
    modelType: ModelConstructorSignature<DataType, ModelType>,
    data: DataType | ModelType
  ): ModelType | null

  public create<ModelType extends BaseModel, DataType extends IBase>(
    modelType: ModelConstructorSignature<DataType, ModelType>,
    data?: DataType | ModelType
  ): ModelType | DataType | null {
    if (!data) {
      data = {} as DataType
    }

    if (!data.id) {
      data.id = uuidv4()
    }

    if (this.idModelMap.value.has(data.id!)) {
      console.warn(`Model with ID ${data.id} already exists in the map.`)
    }

    // If the model was explicitly deleted, do not re-create it.
    if (this.isDeleted(data.id!)) {
      return null
    }

    let returnModel
    // Instantiate the model.
    const newModel = new modelType(data as DataType, true)
    newModel.isManaged = true // Mark the model as managed.

    // It is possible that some client updated the model while it was being loaded from the server.
    // That model will have been placed in the queuedModelUpdates map.
    const queuedModel = this.queuedModelUpdates.value.get(newModel.id!) // Retrieve the queued model.
    if (queuedModel && queuedModel.updatedAt! > newModel.updatedAt!) {
      // If the queued model is newer than the new model, update the new model with the queued model.
      returnModel = this.updateModelWithNewData(newModel, queuedModel, modelType.updateIgnoreFields)
      this.queuedModelUpdates.value.delete(newModel.id!)
    } else {
      returnModel = newModel
    }
    this.idModelMap.value.set(newModel.id!, returnModel)

    // Resolve managed fields on the newly created model.
    this.resolveManagedFields(returnModel)

    // If there are any pending callbacks for this model, call them with the new model.
    if (this.pendingModelLinks.value.get(returnModel.id!)) {
      const callbacks = this.pendingModelLinks.value.get(returnModel.id!)!
      callbacks.forEach((cb) => cb(returnModel))
      this.pendingModelLinks.value.delete(returnModel.id!)
    }

    return returnModel as ModelType
  }

  public updateOrCreate<ModelType extends BaseModel, DataType extends IBase>(
    modelType: ModelConstructorSignature<DataType, ModelType>,
    data: DataType | ModelType
  ): ModelType | null {
    // Check if the model exists in the map.
    if (this.idModelMap.value.has(data.id!)) {
      return this.update(modelType, data)!
    }
    // If it doesn't exist, create a new one.
    return this.create(modelType, data)!
  }

  public update<ModelType extends BaseModel, DataType extends IBase>(
    modelType: ModelConstructorSignature<DataType, ModelType>,
    data: DataType | ModelType
  ): ModelType {
    const existingModel = this.idModelMap.value.get(data.id!)

    const newModel = new modelType(data as DataType, true)
    newModel.isManaged = true // Mark the model as managed.

    // Resolve managed fields
    this.resolveManagedFields(newModel)

    if (existingModel) {
      if (newModel.isShallow() && !existingModel.isShallow()) {
        // Never update a full model with a shallow model representation.
        return newModel
      }

      if (
        existingModel.updatedAt &&
        newModel.updatedAt &&
        newModel.updatedAt.isBefore(existingModel.updatedAt)
      ) {
        // Stale snapshot: the payload was serialized before the state we already
        // hold (e.g. a nested person inside an instruction/KBEC payload that was
        // in flight while the person itself was saved). Merging it would revert
        // fields to their old values, so keep the existing model untouched.
        return existingModel as ModelType
      }

      return this.updateModelWithNewData(
        existingModel,
        newModel,
        modelType.updateIgnoreFields
      ) as ModelType
    }
    this.queueUpdate(newModel) // Queue the update if the model does not exist.
    return newModel
  }

  private updateModelWithNewData<ModelType extends BaseModel>(
    oldModel: ModelType,
    newModel: ModelType,
    ignoreFields?: string[]
  ) {
    if (ignoreFields) {
      Object.keys(newModel).forEach((key) => {
        if (!ignoreFields.includes(key)) {
          if (this.wouldDowngrade(oldModel, newModel, key)) return
          if (this.wouldEraseWithUndefined(oldModel, newModel, key)) return
          ;(oldModel as any)[key] = (newModel as any)[key]
        }
      })
    } else {
      Object.keys(newModel).forEach((key) => {
        if (this.wouldDowngrade(oldModel, newModel, key)) return
        if (this.wouldEraseWithUndefined(oldModel, newModel, key)) return
        ;(oldModel as any)[key] = (newModel as any)[key]
      })
    }
    // Important to return the old model, as this is the memory reference that is being manipulated.
    // newModel is just a temporary reference that does not exist in the map.
    return oldModel
  }

  /**
   * Returns true if assigning newModel[key] would downgrade a fully loaded
   * BaseModel instance to a lazy string reference. We never want to lose
   * a rich object that was already resolved.
   */
  private wouldDowngrade(oldModel: BaseModel, newModel: BaseModel, key: string): boolean {
    const oldVal = (oldModel as any)[key]
    const newVal = (newModel as any)[key]
    return oldVal instanceof BaseModel && typeof newVal === "string"
  }

  /**
   * Returns true if assigning newModel[key] would erase a known value with
   * `undefined`. Model constructors assign every declared field, so a field is
   * `undefined` when the payload simply did not include it (e.g. a serializer
   * that omits Person.citizenNumber on nested representations). An explicit
   * clear from a form arrives as "" or null, never as undefined, so skipping
   * these assignments never blocks a real update.
   */
  private wouldEraseWithUndefined(oldModel: BaseModel, newModel: BaseModel, key: string): boolean {
    const oldVal = (oldModel as any)[key]
    const newVal = (newModel as any)[key]
    return newVal === undefined && oldVal !== undefined
  }

  // Update the model in the queue if it is newer than the existing model.
  private queueUpdate<ModelType extends BaseModel>(model: ModelType) {
    const alreadyQueuedModel = this.queuedModelUpdates.value.get(model.id!)
    // Only queue the update if the model is not already queued or the new update is newer.
    if (!alreadyQueuedModel || alreadyQueuedModel.updatedAt! < model.updatedAt!) {
      this.queuedModelUpdates.value.set(model.id!, model)
    }
  }

  public hasQueuedUpdate(id: string): boolean {
    return this.queuedModelUpdates.value.has(id)
  }

  public getQueuedUpdate(id: string): BaseModel | undefined {
    return this.queuedModelUpdates.value.get(id)
  }

  public delete(id: string) {
    this.deletedModelIDs.value.set(id, true)
    this.idModelMap.value.delete(id)
  }

  /**
   * Evict a model from the in-memory cache WITHOUT marking it as deleted.
   *
   * Use this for poll-driven datasets (e.g. UserLocations) where the same
   * id may be fetched again later and should NOT be blocked by the
   * deleted-ids set — `delete()` is the wrong primitive there because
   * `create()` short-circuits to null when `isDeleted(id)` is true,
   * which would silently drop the next poll's payload.
   *
   * The actual model instance can still be referenced by callers that
   * already held it; eviction just means new lookups via `get()` /
   * `findOrderByInstruction()` won't find it.
   */
  public evict(id: string) {
    this.idModelMap.value.delete(id)
  }

  public evictMany(ids: Iterable<string>) {
    for (const id of ids) {
      this.idModelMap.value.delete(id)
    }
  }

  public exists(id: string): boolean {
    return this.idModelMap.value.has(id)
  }

  public isDeleted(id: string): boolean {
    return this.deletedModelIDs.value.has(id)
  }

  public bulkUpdateOrCreate<ModelType extends BaseModel, DataType extends IBase>(
    modelType: ModelConstructorSignature<DataType, ModelType>,
    data: DataType[]
  ): ModelType[] {
    return (data ?? [])
      .map((item) => this.updateOrCreate(modelType, item))
      .filter((item) => item !== null) as ModelType[]
  }

  /**
   * Resolves managed fields (UUID strings) on a model instance.
   * Reads the model's static `managedResolveFields` to determine which fields to resolve.
   * Only handles the model's own direct fields (1 level depth).
   * Child models are responsible for resolving their own managed fields.
   */
  private resolveManagedFields(model: BaseModel): void {
    const ctor = model.constructor as typeof BaseModel
    const fields = ctor.managedResolveFields
    if (!fields || fields.length === 0) {
      return
    }

    const pending = new Set<string>()

    for (const field of fields) {
      const value = (model as any)[field]

      if (Array.isArray(value)) {
        this.resolveArrayField(value, pending)

        if (!isProductionEnvironment() && value.some((v) => typeof v === "string")) {
          this.installDevWarningForArray(model, field, ctor, value)
        }
      } else if (typeof value === "string") {
        this.resolveScalarField(model, field, value, pending)

        if (!isProductionEnvironment() && pending.has(value)) {
          this.installDevWarning(model, field, ctor)
        }
      }
    }

    if (pending.size > 0) {
      model.whenResolved = this.createResolutionPromise(model, pending)
    }
  }

  /**
   * Resolves UUID strings inside an array field by replacing them with model instances.
   */
  private resolveArrayField(value: any[], pending: Set<string>): void {
    for (let i = 0; i < value.length; i++) {
      if (typeof value[i] !== "string") {
        continue
      }

      const uuid = value[i]
      const idx = i
      pending.add(uuid)
      const resolved = this.get(uuid, (resolvedModel: BaseModel) => {
        value[idx] = resolvedModel
        pending.delete(resolvedModel.id!)
      })
      if (resolved) {
        value[i] = resolved
        pending.delete(resolved.id!)
      }
    }
  }

  /**
   * Resolves a single UUID string field by replacing it with a model instance.
   */
  private resolveScalarField(
    model: BaseModel,
    field: string,
    uuid: string,
    pending: Set<string>
  ): void {
    pending.add(uuid)
    const resolved = this.get(uuid, (resolvedModel: BaseModel) => {
      ;(model as any)[field] = resolvedModel
      pending.delete(resolvedModel.id!)
    })
    if (resolved) {
      ;(model as any)[field] = resolved
      pending.delete(resolved.id!)
    }
  }

  /**
   * DEV-only: installs a getter/setter that warns when accessing a scalar field that is still a UUID string.
   */
  private installDevWarning(model: BaseModel, field: string, ctor: typeof BaseModel): void {
    let currentValue: any = (model as any)[field]
    Object.defineProperty(model, field, {
      configurable: true,
      enumerable: true,
      get() {
        if (typeof currentValue === "string") {
          console.warn(
            `[ManagedResolve] Accessing unresolved field "${field}" on ${ctor.className} (${model.id}) — still a UUID. Await whenFullyResolved() first.`
          )
        }
        return currentValue
      },
      set(v: any) {
        currentValue = v
      }
    })
  }

  /**
   * DEV-only: wraps an array field in a Proxy that warns when accessing an element that is still a UUID string.
   */
  private installDevWarningForArray(
    model: BaseModel,
    field: string,
    ctor: typeof BaseModel,
    value: any[]
  ): void {
    ;(model as any)[field] = new Proxy(value, {
      get(target, prop, receiver) {
        const element = Reflect.get(target, prop, receiver)
        if (typeof prop === "string" && !isNaN(Number(prop)) && typeof element === "string") {
          console.warn(
            `[ManagedResolve] Accessing unresolved element at index ${prop} in array field "${field}" on ${ctor.className} (${model.id}) — still a UUID. Await whenFullyResolved() first.`
          )
        }
        return element
      }
    })
  }

  /**
   * Creates a promise that resolves once all pending UUID references have been resolved,
   * or after a timeout.
   */
  private createResolutionPromise(
    model: BaseModel,
    pending: Set<string>,
    timeoutMs: number = 30_000
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      const startTime = Date.now()
      const check = () => {
        if (pending.size === 0) {
          resolve()
        } else if (Date.now() - startTime > timeoutMs) {
          console.warn(
            `[ManagedResolve] Timeout: ${pending.size} reference(s) unresolved after ${timeoutMs / 1000}s for ${model.getClassName()}`,
            pending
          )
          resolve()
        } else {
          setTimeout(check, RESOLUTION_CHECK_INTERVAL_MS)
        }
      }
      check()
    })
  }
}
